import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listManufacturingRecipeAttachments,
  setManufacturingRecipeAttachments,
  type MfgRecipeSummary,
} from "@/lib/manufacturingClient";
import {
  attachmentRowFromServer,
  emptyAttachmentRow,
  mergeAttachmentPartitions,
  parseAttachmentRowsForSave,
  partitionAttachmentRows,
  recipeKindById,
  type AttachmentUiRow,
} from "@/lib/manufacturing/recipeAttachmentPartition";

export type AttachmentEditorSection = "packages" | "variants";

export type UseManufacturingModelsAttachmentSectionArgs = {
  section: AttachmentEditorSection;
  selectedId: string | null;
  recipes: MfgRecipeSummary[];
  onError: (message: string) => void;
  onSaved?: () => void;
};

function partitionRowsForSection(
  all: AttachmentUiRow[],
  section: AttachmentEditorSection,
  kindById: ReadonlyMap<string, MfgRecipeSummary["kind"]>,
): { rows: AttachmentUiRow[]; siblingRows: AttachmentUiRow[] } {
  const { variantRows, packageRows } = partitionAttachmentRows(all, kindById);
  if (section === "variants") return { rows: variantRows, siblingRows: packageRows };
  return { rows: packageRows, siblingRows: variantRows };
}

export function useManufacturingModelsAttachmentSection({
  section,
  selectedId,
  recipes,
  onError,
  onSaved,
}: UseManufacturingModelsAttachmentSectionArgs) {
  const [rows, setRows] = useState<AttachmentUiRow[]>([]);
  const [siblingRows, setSiblingRows] = useState<AttachmentUiRow[]>([]);
  const [loadBusy, setLoadBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const kindById = useMemo(() => recipeKindById(recipes), [recipes]);
  const recipeKindsKey = useMemo(
    () => recipes.map((r) => `${r.id}:${r.kind ?? "assembly"}`).join("|"),
    [recipes],
  );

  const candidateRecipes = useMemo(() => {
    const pool = recipes.filter((r) => r.id !== selectedId);
    if (section === "variants") return pool.filter((r) => r.kind === "variant");
    return pool.filter((r) => r.kind !== "variant");
  }, [recipes, selectedId, section]);

  const defaultAttachedRecipeId = candidateRecipes[0]?.id ?? "";

  const applyLoadedAttachments = useCallback(
    (all: AttachmentUiRow[]) => {
      const next = partitionRowsForSection(all, section, kindById);
      setRows(next.rows);
      setSiblingRows(next.siblingRows);
    },
    [section, kindById],
  );

  const load = useCallback(async () => {
    if (!selectedId) {
      setRows([]);
      setSiblingRows([]);
      return;
    }
    setLoadBusy(true);
    const res = await listManufacturingRecipeAttachments(selectedId);
    setLoadBusy(false);
    if (!res.ok) {
      setRows([]);
      setSiblingRows([]);
      onErrorRef.current(res.error);
      return;
    }
    applyLoadedAttachments(res.data.attachments.map(attachmentRowFromServer));
  }, [selectedId, applyLoadedAttachments]);

  useEffect(() => {
    if (!selectedId) {
      setRows([]);
      setSiblingRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadBusy(true);
      const res = await listManufacturingRecipeAttachments(selectedId);
      if (cancelled) return;
      setLoadBusy(false);
      if (!res.ok) {
        setRows([]);
        setSiblingRows([]);
        onErrorRef.current(res.error);
        return;
      }
      const all = res.data.attachments.map(attachmentRowFromServer);
      const next = partitionRowsForSection(all, section, kindById);
      setRows(next.rows);
      setSiblingRows(next.siblingRows);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, section, recipeKindsKey, kindById]);

  const addRow = useCallback(() => {
    if (!defaultAttachedRecipeId) return;
    setRows((prev) => [...prev, emptyAttachmentRow(defaultAttachedRecipeId, prev.length)]);
  }, [defaultAttachedRecipeId]);

  const addRowForRecipe = useCallback((recipeId: string) => {
    if (!recipeId) return;
    setRows((prev) => [...prev, emptyAttachmentRow(recipeId, prev.length)]);
  }, []);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const patchRow = useCallback((key: string, patch: Partial<AttachmentUiRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const onSave = useCallback(async () => {
    if (!selectedId) return;
    const merged =
      section === "variants"
        ? mergeAttachmentPartitions(rows, siblingRows)
        : mergeAttachmentPartitions(siblingRows, rows);
    const parsed = parseAttachmentRowsForSave(merged, selectedId);
    if (!parsed.ok) {
      onErrorRef.current(parsed.error);
      return;
    }

    setSaveBusy(true);
    const res = await setManufacturingRecipeAttachments({
      parent_recipe_id: selectedId,
      attachments: parsed.attachments,
    });
    setSaveBusy(false);
    if (!res.ok) {
      onErrorRef.current(res.error);
      return;
    }
    await load();
    onSavedRef.current?.();
  }, [selectedId, rows, siblingRows, section, load]);

  return {
    rows,
    loadBusy,
    saveBusy,
    candidateRecipes,
    defaultAttachedRecipeId,
    reload: load,
    addRow,
    addRowForRecipe,
    removeRow,
    patchRow,
    onSave,
  };
}

export type ManufacturingModelsAttachmentSectionModel = ReturnType<
  typeof useManufacturingModelsAttachmentSection
>;
